import { SubCapabilityKey } from "@next-platform/packages-ts-types";
import { useCallback, useMemo } from "react";
import useEventCapabilityIsUsed from "../../../services/databricks/hooks/useEventCapabilityIsUsed";
import { useAppDispatch, useAppSelector } from "../../hook";
import { closeViewMediaModal, openViewMediaModal, setViewMediaModalFileId } from "./viewMediaModalSlice";
/**
 * The details modal, for whichever surface opens it. The caller passes the file and the order it
 * displayed; the modal does not know which page that was.
 *
 * Stepping is a pure index walk over that list, so a file the caller never listed simply has no
 * neighbours rather than falling off the end. Each completed step raises the navigation usage event;
 * a press that cannot move raises nothing.
 */
const useViewMediaModal = () => {
    const dispatch = useAppDispatch();
    const raiseEventCapabilityIsUsed = useEventCapabilityIsUsed();
    const isOpen = useAppSelector((state) => state.viewMediaModal.isOpen);
    const fileId = useAppSelector((state) => state.viewMediaModal.fileId);
    const fileIds = useAppSelector((state) => state.viewMediaModal.fileIds);
    // -1 both when there is no list and when the open file is not in it; every guard below reads that
    // as "no neighbours", which is what a single-file open should do.
    const currentIndex = useMemo(() => (fileId === null ? -1 : fileIds.indexOf(fileId)), [fileId, fileIds]);
    const hasNext = currentIndex >= 0 && currentIndex < fileIds.length - 1;
    const hasPrevious = currentIndex > 0;
    const position = useMemo(() => (currentIndex < 0 ? null : { current: currentIndex + 1, total: fileIds.length }), [currentIndex, fileIds.length]);
    const open = useCallback((nextFileId, nextFileIds) => {
        dispatch(openViewMediaModal(nextFileIds === undefined ? { fileId: nextFileId } : { fileId: nextFileId, fileIds: nextFileIds }));
    }, [dispatch]);
    const close = useCallback(() => {
        dispatch(closeViewMediaModal());
    }, [dispatch]);
    const next = useCallback(() => {
        if (!hasNext) {
            return;
        }
        raiseEventCapabilityIsUsed(SubCapabilityKey.GalleryMediaNavigation);
        dispatch(setViewMediaModalFileId(fileIds[currentIndex + 1]));
    }, [dispatch, raiseEventCapabilityIsUsed, hasNext, fileIds, currentIndex]);
    const previous = useCallback(() => {
        if (!hasPrevious) {
            return;
        }
        raiseEventCapabilityIsUsed(SubCapabilityKey.GalleryMediaNavigation);
        dispatch(setViewMediaModalFileId(fileIds[currentIndex - 1]));
    }, [dispatch, raiseEventCapabilityIsUsed, hasPrevious, fileIds, currentIndex]);
    return { isOpen, fileId, position, hasNext, hasPrevious, open, close, next, previous };
};
export default useViewMediaModal;
